// Bugfix 31 en 32 (#1403, #1559): de gedeelde schema-editor van de Template
// Maker en van het persoonlijke schema van een sporter.
//
// - Tabbladen wisselen zonder iets te laden: alle sessies staan in de pagina.
// - Elk veld bewaart zichzelf bij wijzigen (fetch). Een veld geeft JSON terug,
//   zodat de cursor blijft staan; een wijziging in de opbouw (sets, reps,
//   oefeningen, volgorde, sessies) geeft een Turbo Stream die het werkvlak
//   ververst. Verzoeken gaan op volgorde, één tegelijk.
// - "Opslaan" wacht tot alles bewaard is. Vertrek je met iets dat nog bezig
//   is of mislukte, dan waarschuwt de browser.
// - Slepen: een regel naar een andere plek, of een oefening uit de kiezer
//   naar een plek in de sessie. Valt hij binnen een superset (tussen A1 en
//   A2), dan hoort hij daarbij; de server bepaalt de nummering.
// - #1651: in het schema van een sporter is er een tabblad per trainingsweek
//   en staan alle sessies van die week onder elkaar. Doorkopiëren, dupliceren,
//   verwijderen, QR en "Oefening toevoegen" staan in de kop of onder elke
//   sessie en gelden voor die sessie. "actief" is de sessie waarin de trainer
//   het laatst iets deed; die blijft open na een verversing en daarvoor geldt
//   de QR uit het menu.
import { Controller } from "stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const JSON_TYPE = "application/json"
const STREAM_TYPE = "text/vnd.turbo-stream.html, text/html, application/xhtml+xml"

export default class extends Controller {
  static targets = ["werkvlak", "tab", "paneel", "lijst", "regel", "status", "kiezer", "bevestig", "verwijderKeuze",
    "controle", "voorbeeld", "titelblok", "sessieKeuze", "doorkopieer", "qr", "qrFrame", "vorigeWeek", "volgendeWeek",
    "weekPositie"]

  static values = { modus: String, sessieToevoegenUrl: String, sessiesVolgordeUrl: String, opslaanUrl: String,
    extra: Object, teksten: Object }

  connect() {
    this.bezig = 0
    this.fouten = 0
    this.wachtrij = Promise.resolve()
    this.actief = this.hasWerkvlakTarget ? this.werkvlakTarget.dataset.actief : null
  }

  // Na een Turbo Stream staat er een nieuw werkvlak; de server heeft de
  // juiste sessie al open gezet.
  werkvlakTargetConnected(werkvlak) {
    if (werkvlak.dataset.actief) this.actief = werkvlak.dataset.actief
    this.werkActiefBij()
    if (this.herstelFocus) {
      const veld = this.zoekVeld(this.herstelFocus)
      this.herstelFocus = null
      if (veld) veld.focus({ preventScroll: true })
    }
    if (this.naToevoegen) {
      this.naToevoegen = false
      const knop = this.actiefPaneel()?.querySelector(".se-toevoegen")
      if (knop) knop.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  // ---- verzoeken ----------------------------------------------------------

  csrf() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  stuur(url, { methode = "PATCH", velden = {}, stream = false } = {}) {
    const taak = async () => {
      this.bezig += 1
      this.toonStatus()
      const body = new FormData()
      if (methode !== "POST") body.append("_method", methode.toLowerCase())
      Object.entries(this.extraValue || {}).forEach(([sleutel, waarde]) => body.append(sleutel, waarde))
      if (this.actief) body.append("actief", this.actief)
      Object.entries(velden).forEach(([sleutel, waarde]) => {
        if (Array.isArray(waarde)) waarde.forEach((w) => body.append(sleutel, w))
        else if (waarde !== undefined && waarde !== null) body.append(sleutel, waarde)
      })
      let antwoord = null
      try {
        antwoord = await fetch(url, {
          method: "POST",
          headers: {
            Accept: stream ? STREAM_TYPE : JSON_TYPE,
            "X-CSRF-Token": this.csrf(),
            "X-Requested-With": "XMLHttpRequest"
          },
          body,
          credentials: "same-origin"
        })
        if (stream) {
          const html = await antwoord.text()
          if (html.includes("<turbo-stream")) {
            this.herstelFocus = this.herstelFocus || this.focusSleutel(document.activeElement)
            Turbo.renderStreamMessage(html)
          }
        }
        if (!antwoord.ok) throw new Error(`HTTP ${antwoord.status}`)
        this.fouten = 0
        return antwoord
      } catch (fout) {
        this.fouten += 1
        return null
      } finally {
        this.bezig -= 1
        this.toonStatus()
      }
    }
    const resultaat = this.wachtrij.then(taak, taak)
    this.wachtrij = resultaat.catch(() => null)
    return resultaat
  }

  toonStatus(tekst = null) {
    if (!this.hasStatusTarget) return
    const t = this.tekstenValue
    this.statusTarget.classList.toggle("is-fout", this.fouten > 0 && this.bezig === 0)
    if (tekst) this.statusTarget.textContent = tekst
    else if (this.bezig > 0) this.statusTarget.textContent = t.opslaan
    else if (this.fouten > 0) this.statusTarget.textContent = t.fout
    else this.statusTarget.textContent = t.opgeslagen
  }

  melding(tekst) {
    const plek = document.getElementById("se-melding")
    if (plek) plek.textContent = tekst || ""
  }

  focusSleutel(element) {
    if (!element || !this.element.contains(element)) return null
    const regel = element.closest(".se-regel")
    if (regel && element.dataset.veld) {
      const set = element.closest("[data-set-id]")
      return { regel: regel.dataset.id, veld: element.dataset.veld, set: set ? set.dataset.setId : null }
    }
    if (element.dataset.sessieVeld) return { paneel: this.actief, sessieVeld: element.dataset.sessieVeld }
    return null
  }

  zoekVeld(sleutel) {
    if (sleutel.regel) {
      const regel = document.getElementById(`se-regel-${sleutel.regel}`)
      if (!regel) return null
      if (sleutel.set) {
        const set = regel.querySelector(`[data-set-id="${sleutel.set}"] [data-veld="${sleutel.veld}"]`)
        if (set) return set
      }
      return regel.querySelector(`.se-velden [data-veld="${sleutel.veld}"]`)
    }
    if (sleutel.sessieVeld) return this.actiefPaneel()?.querySelector(`[data-sessie-veld="${sleutel.sessieVeld}"]`)
    return null
  }

  markeer(veld, gelukt) {
    veld.classList.toggle("is-fout", !gelukt)
    if (!gelukt) return
    veld.classList.add("is-bewaard")
    setTimeout(() => veld.classList.remove("is-bewaard"), 900)
  }

  // ---- tabbladen ----------------------------------------------------------

  actiefPaneel() {
    return this.paneelTargets.find((paneel) => paneel.dataset.key === this.actief) || this.paneelTargets[0]
  }

  // #1651: tabbladen per trainingsweek (alleen het schema van een sporter)
  weken() {
    return this.hasWerkvlakTarget && this.werkvlakTarget.dataset.weken === "1"
  }

  weekNummers() {
    return this.tabTargets.map((tab) => tab.dataset.week).filter((week) => week)
  }

  kiesWeek(event) {
    this.toonWeek(event.currentTarget.dataset.week)
  }

  vorigeWeek() {
    this.stapWeek(-1)
  }

  volgendeWeek() {
    this.stapWeek(1)
  }

  stapWeek(stap) {
    const weken = this.weekNummers()
    const index = weken.indexOf(this.actiefPaneel()?.dataset.week) + stap
    if (index < 0 || index >= weken.length) return
    this.toonWeek(weken[index])
    this.werkvlakTarget.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  // De eerste open sessie van de week wordt de actieve sessie.
  toonWeek(week) {
    const panelen = this.paneelTargets.filter((paneel) => paneel.dataset.week === String(week))
    if (panelen.length === 0) return
    const paneel = panelen.find((p) => p.dataset.bewerkbaar === "1") || panelen[0]
    this.actief = paneel.dataset.key
    this.werkActiefBij()
    this.zetUrl()
  }

  // Klikken of tabben in een sessie van de week maakt hem de actieve sessie
  // (zonder zichtbare selectie): die blijft open na een verversing.
  kiesPaneel(event) {
    const paneel = event.currentTarget
    if (!this.weken() || paneel.dataset.key === this.actief) return
    this.actief = paneel.dataset.key
    this.werkActiefBij({ tabInBeeld: false })
    this.zetUrl()
  }

  zetUrl() {
    const url = new URL(window.location.href)
    url.searchParams.set("sessie", this.actief)
    window.history.replaceState(window.history.state, "", url.toString())
  }

  // Alleen horizontaal in de tabbalk schuiven; de pagina zelf blijft staan.
  tabInBeeld(tab) {
    const balk = tab.parentElement
    if (!balk) return
    const links = tab.offsetLeft - balk.offsetLeft
    if (links < balk.scrollLeft) balk.scrollLeft = links
    else if (links + tab.offsetWidth > balk.scrollLeft + balk.clientWidth) {
      balk.scrollLeft = links + tab.offsetWidth - balk.clientWidth
    }
  }

  kiesTab(event) {
    this.actief = event.currentTarget.dataset.key
    this.werkActiefBij()
    const url = new URL(window.location.href)
    url.searchParams.set("sessie", this.actief)
    window.history.replaceState(window.history.state, "", url.toString())
  }

  tabToets(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return
    event.preventDefault()
    const tabs = this.tabTargets
    let index = tabs.indexOf(event.currentTarget)
    if (event.key === "ArrowLeft") index = Math.max(0, index - 1)
    if (event.key === "ArrowRight") index = Math.min(tabs.length - 1, index + 1)
    if (event.key === "Home") index = 0
    if (event.key === "End") index = tabs.length - 1
    tabs[index].focus()
    tabs[index].click()
  }

  werkActiefBij({ tabInBeeld = true } = {}) {
    const paneel = this.actiefPaneel()
    if (paneel) this.actief = paneel.dataset.key
    const weken = this.weken()
    const week = paneel ? paneel.dataset.week : null
    const zichtbaar = (p) => (weken ? p.dataset.week === week : p === paneel)
    this.paneelTargets.forEach((p) => {
      p.hidden = !zichtbaar(p)
    })
    const hoort = (tab) => (weken ? tab.dataset.week === week : tab.dataset.key === this.actief)
    this.tabTargets.forEach((tab) => {
      const aan = hoort(tab)
      tab.setAttribute("aria-selected", aan ? "true" : "false")
      tab.tabIndex = aan ? 0 : -1
    })
    const tab = this.tabTargets.find(hoort)
    if (tab && tabInBeeld) {
      if (weken) this.tabInBeeld(tab)
      else tab.scrollIntoView({ block: "nearest", inline: "nearest" })
    }
    if (weken) this.werkWeekBij(paneel)
    if (this.hasVoorbeeldTarget && paneel && paneel.dataset.voorbeeldUrl) {
      this.voorbeeldTarget.href = paneel.dataset.voorbeeldUrl
    }
    if (this.hasKiezerTarget && paneel) {
      const plek = this.kiezerTarget.querySelector("[data-oefening-kiezer-target='sessie']")
      if (plek) plek.textContent = paneel.dataset.titel
    }
  }

  werkWeekBij(paneel) {
    const weken = this.weekNummers()
    const index = paneel ? weken.indexOf(paneel.dataset.week) : -1
    if (this.hasVorigeWeekTarget) this.vorigeWeekTarget.disabled = index <= 0
    if (this.hasVolgendeWeekTarget) this.volgendeWeekTarget.disabled = index < 0 || index >= weken.length - 1
    if (this.hasWeekPositieTarget && index >= 0) {
      this.weekPositieTarget.textContent = this.weekPositieTarget.dataset.sjabloon.replace("%{nr}", weken[index])
    }
  }

  voegSessieToe() {
    this.stuur(this.sessieToevoegenUrlValue, { methode: "POST", stream: true })
  }

  // De knoppen in de kop van een sessie gelden voor die sessie; die wordt ook
  // de actieve sessie, zodat zijn week na de verversing open blijft.
  sessieVan(event) {
    const paneel = event?.currentTarget?.closest("[data-schema-editor-target='paneel']") || this.actiefPaneel()
    if (paneel) this.actief = paneel.dataset.key
    return paneel
  }

  dupliceerSessie(event) {
    const paneel = this.sessieVan(event)
    if (paneel) this.stuur(paneel.dataset.dupliceerUrl, { methode: "POST", stream: true })
  }

  async verwijderSessie(event) {
    const paneel = this.sessieVan(event)
    if (!paneel) return
    if (paneel.dataset.kanVerwijderen !== "1") {
      this.melding(this.tekstenValue.niet_verwijderbaar)
      return
    }
    const t = this.tekstenValue
    const tekst = t.sessie_verwijderen_tekst.replace("%{sessie}", paneel.dataset.titel)
    if (this.modusValue !== "sporter" || !this.hasSessieKeuzeTarget) {
      const ja = await this.vraag(t.sessie_verwijderen, tekst)
      if (ja) this.stuur(paneel.dataset.verwijderUrl, { methode: "DELETE", stream: true })
      return
    }
    // #1559: alleen deze sessie of alle open sessies van deze training. Geen
    // voorgekozen knop; zonder andere open sessies is er alleen "deze".
    const dialoog = this.sessieKeuzeTarget
    const training = this.trainingNaam(paneel)
    const alle = this.paneelTargets.filter((p) => p.dataset.fase === paneel.dataset.fase && p.dataset.bewerkbaar === "1" &&
      (p === paneel || p.dataset.gestart !== "1"))
    const knop = dialoog.querySelector("[data-alle-knop]")
    const uitleg = dialoog.querySelector("[data-alle-tekst]")
    knop.hidden = alle.length < 2
    knop.textContent = t.sessie_alle_knop.replace("%{aantal}", alle.length)
    uitleg.textContent = alle.length < 2 ? t.sessie_alle_geen.replace("%{training}", training)
      : t.sessie_alle_tekst.replace("%{training}", training).replace("%{sessies}", alle.map((p) => p.dataset.nr).join(", "))
    const keuze = await this.kies(dialoog, tekst)
    if (keuze === "sessie" || (keuze === "overal" && alle.length > 1)) {
      this.stuur(paneel.dataset.verwijderUrl, { methode: "DELETE", velden: { overal: keuze === "overal" ? "1" : "0" },
        stream: true })
    }
  }

  trainingNaam(paneel) {
    const veld = paneel.querySelector(".se-instellingen-velden [data-sessie-veld='naam']")
    return (veld && (veld.value || veld.placeholder)) || ""
  }

  // #1559 (Sam): deze sessie naar volgende open sessies kopiëren, van
  // dezelfde training (standaard) of een andere training van dit schema. Ook
  // vanuit een afgeronde of begonnen sessie; de doelen zijn altijd open.
  async kopieerDoor(event) {
    const paneel = this.sessieVan(event)
    if (!paneel || !this.hasDoorkopieerTarget || !paneel.dataset.doorkopieerUrl) return
    const t = this.tekstenValue
    const dialoog = this.doorkopieerTarget
    const keuze = dialoog.querySelector("[data-training-keuze]")
    keuze.innerHTML = ""
    const fases = []
    this.paneelTargets.forEach((p) => {
      if (p.dataset.fase && !fases.some((f) => f.fase === p.dataset.fase)) fases.push({ fase: p.dataset.fase, paneel: p })
    })
    fases.sort((a, b) => Number(a.fase) - Number(b.fase)).forEach(({ fase, paneel: p }) => {
      const optie = document.createElement("option")
      optie.value = fase
      const naam = this.trainingNaam(p)
      optie.textContent = fase === paneel.dataset.fase ? t.kopieer_huidig.replace("%{training}", naam) : naam
      keuze.appendChild(optie)
    })
    keuze.value = paneel.dataset.fase
    this.kopieerBron = paneel
    this.vulDoorkopieerLijst()
    const tekst = t.kopieer_tekst.replace("%{sessie}", paneel.dataset.titel)
    const antwoord = await this.kies(dialoog, tekst)
    this.kopieerBron = null
    if (antwoord !== "ja") return
    const gekozen = [...dialoog.querySelectorAll("[data-lijst] input:checked")].map((vak) => vak.value)
    if (gekozen.length === 0) return
    this.stuur(paneel.dataset.doorkopieerUrl, { methode: "POST", velden: { "doelen[]": gekozen }, stream: true })
  }

  // De open sessies van de gekozen training, per week ("Week 3 · Training B").
  // Van dezelfde training alleen de sessies na de bron. Van een andere
  // training ook de sessies die eerder in het schema staan (Sam, #1559); die
  // staan standaard uit, zodat er niet per ongeluk terug wordt gekopieerd.
  vulDoorkopieerLijst() {
    const bron = this.kopieerBron
    if (!bron) return
    const t = this.tekstenValue
    const dialoog = this.doorkopieerTarget
    const fase = dialoog.querySelector("[data-training-keuze]").value
    const lijst = dialoog.querySelector("[data-lijst]")
    const geen = dialoog.querySelector("[data-geen]")
    const ja = dialoog.querySelector("[data-ja]")
    const eerderUitleg = dialoog.querySelector("[data-eerder]")
    const sessieNr = (p) => Number(p.dataset.key.split("-")[0])
    const nr = sessieNr(bron)
    const zelfde = fase === bron.dataset.fase
    const doelen = this.paneelTargets.filter((p) => p !== bron && p.dataset.fase === fase &&
      (!zelfde || sessieNr(p) > nr) && p.dataset.bewerkbaar === "1" && p.dataset.gestart !== "1")
      .sort((a, b) => sessieNr(a) - sessieNr(b))
    const eerder = doelen.filter((doel) => sessieNr(doel) < nr)
    lijst.innerHTML = ""
    doelen.forEach((doel) => {
      const label = document.createElement("label")
      const vak = document.createElement("input")
      vak.type = "checkbox"
      vak.value = doel.dataset.key
      vak.checked = sessieNr(doel) > nr
      if (sessieNr(doel) < nr) label.dataset.eerder = "1"
      label.appendChild(vak)
      const tekst = t.kopieer_doel.replace("%{week}", doel.dataset.trainingWeek).replace("%{training}", this.trainingNaam(doel))
        .replace("%{nr}", doel.dataset.nr)
      label.appendChild(document.createTextNode(` ${tekst}`))
      lijst.appendChild(label)
    })
    const training = this.paneelTargets.find((p) => p.dataset.fase === fase) || bron
    geen.hidden = doelen.length > 0
    if (eerderUitleg) {
      eerderUitleg.hidden = eerder.length === 0
      eerderUitleg.textContent = t.kopieer_eerder
    }
    geen.textContent = t.kopieer_geen.replace("%{training}", this.trainingNaam(training))
    if (ja) ja.disabled = doelen.length === 0
  }

  // #1559: de QR-code van de open sessie; het venster laadt hem pas bij openen.
  toonQr(event) {
    const paneel = event?.currentTarget?.closest("[data-schema-editor-target='paneel']") || this.actiefPaneel()
    if (!paneel || !paneel.dataset.qrUrl || !this.hasQrTarget) return
    this.element.querySelectorAll("details.se-menu[open]").forEach((menu) => menu.removeAttribute("open"))
    const frame = this.qrFrameTarget
    if (this.qrLaden === undefined) this.qrLaden = frame.innerHTML
    if (frame.getAttribute("src") !== paneel.dataset.qrUrl) {
      frame.innerHTML = this.qrLaden
      frame.setAttribute("src", paneel.dataset.qrUrl)
    }
    this.kies(this.qrTarget)
  }

  // #1559: de sessienaam direct in de kop wijzigen (potlood). In de Template
  // Maker staat er geen veld in de kop; daar gaat de focus naar de instellingen.
  bewerkSessieNaam(event) {
    const paneel = this.actiefPaneel()
    const kop = event?.currentTarget?.closest("h2")
    const invoer = kop?.querySelector("[data-sessie-naam-invoer]")
    if (invoer) {
      kop.querySelector("[data-sessie-titel]").hidden = true
      event.currentTarget.hidden = true
      invoer.hidden = false
      invoer.focus()
      invoer.select()
      return
    }
    const veld = paneel?.querySelector("[data-sessie-veld='naam']")
    if (!veld) return
    veld.scrollIntoView({ block: "center", behavior: "smooth" })
    veld.focus({ preventScroll: true })
    veld.select()
  }

  sluitSessieNaam(event) {
    const invoer = event.currentTarget
    if (invoer.hidden) return
    if (event.type === "keydown") {
      event.preventDefault()
      invoer.blur()
      return
    }
    const kop = invoer.closest("h2")
    invoer.hidden = true
    kop.querySelector("[data-sessie-titel]").hidden = false
    const knop = kop.querySelector("[data-sessie-naam-knop]")
    if (knop) knop.hidden = false
  }

  async bewaarSessie(event) {
    const veld = event.target
    const paneel = veld.closest("[data-schema-editor-target='paneel']")
    if (!paneel) return
    const antwoord = await this.stuur(paneel.dataset.updateUrl, { velden: { [veld.dataset.sessieVeld]: veld.value } })
    this.markeer(veld, !!antwoord)
    if (!antwoord) return
    const data = await antwoord.json()
    if (this.modusValue === "sporter") this.werkFaseBij(paneel, data, veld)
    else {
      if (veld.dataset.sessieVeld === "naam") {
        paneel.querySelectorAll("[data-sessie-veld='naam']").forEach((naam) => { if (naam !== veld) naam.value = veld.value })
      }
      this.werkSessieBij(paneel, data)
    }
  }

  // Template: alleen deze sessie.
  werkSessieBij(paneel, data) {
    const titel = data.titel || data.naam
    paneel.dataset.titel = titel
    paneel.querySelector("[data-sessie-titel]").textContent = titel
    const tab = this.tabTargets.find((t) => t.dataset.key === paneel.dataset.key)
    if (tab) tab.querySelector("[data-tab-titel]").textContent = titel
    this.zetNotitie(paneel, data.notitie)
  }

  // Sporter: naam en notitie horen bij de training, dus bij alle sessies
  // van die training.
  werkFaseBij(bron, data, veld = null) {
    this.paneelTargets.forEach((paneel, index) => {
      if (paneel.dataset.key.split("-")[1] !== String(data.fase)) return
      const titel = this.tekstenValue.sessie_titel.replace("%{nr}", index + 1).replace("%{naam}", data.naam)
      paneel.dataset.titel = titel
      paneel.querySelector("[data-sessie-titel]").textContent = titel
      const tab = this.tabTargets.find((t) => t.dataset.key === paneel.dataset.key)
      if (tab) tab.querySelector("[data-tab-titel]").textContent = titel
      this.zetNotitie(paneel, data.notitie)
      if (veld && veld.dataset.sessieVeld === "naam") {
        paneel.querySelectorAll("[data-sessie-veld='naam']").forEach((naam) => {
          if (naam !== veld) naam.value = veld.value
        })
      }
      if (paneel !== bron) {
        const notitie = paneel.querySelector("[data-sessie-veld='notitie']")
        if (notitie) notitie.value = data.notitie || ""
      }
    })
    const dagen = this.element.querySelector("[data-trainingsdagen]")
    if (dagen && data.dagen !== undefined) {
      dagen.textContent = data.dagen || ""
      dagen.hidden = !data.dagen
    }
  }

  zetNotitie(paneel, notitie) {
    const plek = paneel.querySelector("[data-sessie-notitie]")
    if (!plek) return
    plek.textContent = notitie || ""
    plek.hidden = !notitie
  }

  // ---- velden in een regel ------------------------------------------------

  async bewaarVeld(event) {
    const veld = event.target
    const regel = veld.closest(".se-regel")
    if (!regel || !veld.dataset.veld) return
    const structureel = veld.dataset.structureel === "1"
    const antwoord = await this.stuur(regel.dataset.url, {
      velden: { [`oefening[${veld.dataset.veld}]`]: veld.value },
      stream: structureel
    })
    if (!document.body.contains(veld)) return
    this.markeer(veld, !!antwoord)
    if (!antwoord || structureel) return
    const data = await antwoord.json()
    if (veld.dataset.veld === "rest" && document.activeElement !== veld) {
      veld.value = data.rest === null || data.rest === undefined ? "" : this.tekstenValue.rust.replace("%{seconden}", data.rest)
    }
    if (veld.dataset.veld === "tempo" && document.activeElement !== veld) veld.value = data.tempo || ""
    this.vervangOverzicht(data.overzicht)
  }

  async bewaarSet(event) {
    const veld = event.target
    const regel = veld.closest(".se-regel")
    const set = veld.closest("[data-set-id]")
    if (!regel || !set) return
    const sleutel = `oefening[set_rows][${set.dataset.setId}][${veld.dataset.veld}]`
    const antwoord = await this.stuur(regel.dataset.url, { velden: { [sleutel]: veld.value } })
    this.markeer(veld, !!antwoord)
  }

  vervangOverzicht(html) {
    if (!html) return
    const sjabloon = document.createElement("template")
    sjabloon.innerHTML = html.trim()
    const nieuw = sjabloon.content.firstElementChild
    const oud = nieuw && document.getElementById(nieuw.id)
    if (oud) oud.replaceWith(nieuw)
  }

  koppel(event) {
    const knop = event.currentTarget
    this.stuur(knop.dataset.url, { velden: { wissel: "1" }, stream: true })
  }

  wisselWarmup(event) {
    const knop = event.currentTarget
    const regel = knop.closest(".se-regel")
    const aan = knop.dataset.aan === "1" ? "0" : "1"
    this.stuur(regel.dataset.url, { velden: { "oefening[warmup_enabled]": aan }, stream: true })
  }

  async verwijderOefening(event) {
    const knop = event.currentTarget
    const regel = knop.closest(".se-regel")
    if (this.modusValue !== "sporter") {
      this.stuur(regel.dataset.url, { methode: "DELETE", stream: true })
      return
    }
    const keuze = await this.kies(this.verwijderKeuzeTarget,
      this.tekstenValue.oefening_verwijderen_tekst.replace("%{naam}", knop.dataset.naam || ""))
    if (keuze === "sessie" || keuze === "overal") {
      this.stuur(regel.dataset.url, { methode: "DELETE", velden: { overal: keuze === "overal" ? "1" : "0" }, stream: true })
    }
  }

  // ---- oefeningen toevoegen -----------------------------------------------

  openKiezer(event) {
    if (!this.hasKiezerTarget) return
    const paneel = event?.currentTarget?.closest("[data-schema-editor-target='paneel']")
    if (paneel) this.actief = paneel.dataset.key
    this.werkActiefBij({ tabInBeeld: !this.weken() })
    this.kiezerTarget.hidden = false
    const zoek = this.kiezerTarget.querySelector("input[type='search']")
    if (zoek) {
      zoek.focus()
      zoek.select()
    }
  }

  kiezerDicht() {
    const knop = this.actiefPaneel()?.querySelector(".se-toevoegen")
    if (knop) knop.focus({ preventScroll: true })
  }

  voegOefeningToe(event) {
    const paneel = this.actiefPaneel()
    if (!paneel || paneel.dataset.bewerkbaar !== "1") return
    this.naToevoegen = true
    this.stuur(paneel.dataset.toevoegenUrl, { methode: "POST", velden: { exercise_type_id: event.detail.id }, stream: true })
    this.melding(`${this.tekstenValue.toegevoegd}: ${event.detail.naam || ""}`)
    if (window.innerWidth < 760 && this.hasKiezerTarget) this.kiezerTarget.hidden = true
  }

  // ---- slepen: regels en oefeningen uit de kiezer ---------------------------

  pakVast(event) {
    const regel = event.currentTarget.closest(".se-regel")
    if (!regel) return
    regel.draggable = true
    // Zonder slepen loslaten: de regel weer gewoon maken, zodat tekst in de
    // velden selecteerbaar blijft.
    document.addEventListener("pointerup", () => {
      if (this.gesleept !== regel) regel.draggable = false
    }, { once: true })
  }

  sleepStart(event) {
    const regel = event.target
    if (!regel.classList || !regel.classList.contains("se-regel")) return
    this.gesleept = regel
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", regel.dataset.id)
    setTimeout(() => regel.classList.add("is-sleept"), 0)
  }

  sleepEinde() {
    if (this.gesleept) {
      this.gesleept.classList.remove("is-sleept")
      this.gesleept.draggable = false
    }
    this.gesleept = null
    this.ruimPlekOp()
  }

  isKiezerSleep(event) {
    return Array.from(event.dataTransfer?.types || []).includes("application/x-oefening")
  }

  sleepOver(event) {
    if (!this.gesleept && !this.isKiezerSleep(event)) return
    const lijst = event.currentTarget
    if (lijst.closest("[data-bewerkbaar='0']")) return
    // #1651: een regel verplaatsen kan alleen binnen zijn eigen sessie
    if (this.gesleept && !lijst.contains(this.gesleept)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = this.gesleept ? "move" : "copy"
    const regels = Array.from(lijst.querySelectorAll(".se-regel")).filter((r) => r !== this.gesleept)
    const onder = regels.find((r) => {
      const vak = r.getBoundingClientRect()
      return event.clientY < vak.top + vak.height / 2
    })
    const plek = this.plek()
    if (onder) {
      if (plek.nextElementSibling !== onder) onder.parentNode.insertBefore(plek, onder)
    } else {
      const groepen = lijst.querySelectorAll(".se-groep")
      const laatste = groepen[groepen.length - 1]
      const doel = laatste || lijst
      if (doel.lastElementChild !== plek) doel.appendChild(plek)
    }
  }

  sleepWeg(event) {
    const lijst = event.currentTarget
    if (event.relatedTarget && lijst.contains(event.relatedTarget)) return
    this.ruimPlekOp()
  }

  plek() {
    if (!this.plekElement) {
      this.plekElement = document.createElement("div")
      this.plekElement.className = "se-plek"
      this.plekElement.setAttribute("aria-hidden", "true")
    }
    return this.plekElement
  }

  ruimPlekOp() {
    if (this.plekElement && this.plekElement.parentNode) this.plekElement.remove()
  }

  neerzetten(event) {
    const lijst = event.currentTarget
    const plek = this.plekElement
    if (!plek || !lijst.contains(plek)) return
    event.preventDefault()
    const volgorde = []
    lijst.querySelectorAll(".se-regel, .se-plek").forEach((el) => {
      if (el === plek) volgorde.push("PLEK")
      else if (el !== this.gesleept) volgorde.push(el.dataset.id)
    })
    const positie = volgorde.indexOf("PLEK")
    const paneel = lijst.closest("[data-schema-editor-target='paneel']")
    this.ruimPlekOp()

    if (this.gesleept) {
      const id = this.gesleept.dataset.id
      const ids = volgorde.map((v) => (v === "PLEK" ? id : v))
      this.sleepEinde()
      this.stuur(paneel.dataset.volgordeUrl, { velden: { "ids[]": ids, verplaatst: id }, stream: true })
      return
    }
    const typeId = event.dataTransfer.getData("application/x-oefening")
    if (!typeId) return
    this.stuur(paneel.dataset.toevoegenUrl, {
      methode: "POST", velden: { exercise_type_id: typeId, positie: positie }, stream: true
    })
  }

  // Zonder muis: pijltje omhoog of omlaag op de greep verplaatst de regel.
  toetsVerplaats(event) {
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return
    event.preventDefault()
    const regel = event.currentTarget.closest(".se-regel")
    const lijst = regel.closest("[data-schema-editor-target='lijst']")
    const paneel = regel.closest("[data-schema-editor-target='paneel']")
    const ids = Array.from(lijst.querySelectorAll(".se-regel")).map((r) => r.dataset.id)
    const index = ids.indexOf(regel.dataset.id)
    const doel = event.key === "ArrowUp" ? index - 1 : index + 1
    if (doel < 0 || doel >= ids.length) return
    ids.splice(index, 1)
    ids.splice(doel, 0, regel.dataset.id)
    this.herstelFocus = { regel: regel.dataset.id, veld: null }
    this.stuur(paneel.dataset.volgordeUrl, { velden: { "ids[]": ids, verplaatst: regel.dataset.id }, stream: true })
      .then(() => {
        const greep = document.querySelector(`#se-regel-${regel.dataset.id} .se-greep`)
        if (greep) greep.focus()
      })
  }

  // ---- tabbladen slepen (Template Maker) ------------------------------------

  tabStart(event) {
    this.gesleepteTab = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.gesleepteTab.dataset.key)
    this.gesleepteTab.classList.add("is-sleept")
  }

  tabOver(event) {
    if (!this.gesleepteTab) return
    event.preventDefault()
    const doel = event.target.closest(".se-tab")
    if (!doel || doel === this.gesleepteTab) return
    const vak = doel.getBoundingClientRect()
    const erna = event.clientX > vak.left + vak.width / 2
    doel.parentNode.insertBefore(this.gesleepteTab, erna ? doel.nextSibling : doel)
  }

  tabNeerzetten(event) {
    if (!this.gesleepteTab) return
    event.preventDefault()
    const ids = this.tabTargets.map((tab) => tab.dataset.key)
    this.tabEinde()
    this.stuur(this.sessiesVolgordeUrlValue, { velden: { "ids[]": ids }, stream: true })
  }

  tabEinde() {
    if (this.gesleepteTab) this.gesleepteTab.classList.remove("is-sleept")
    this.gesleepteTab = null
  }

  // ---- gegevens bovenaan --------------------------------------------------

  async bewaarGegevens(event) {
    const veld = event.target
    const blok = veld.closest("[data-url]")
    const antwoord = await this.stuur(blok.dataset.url, { velden: { [`template[${veld.name}]`]: veld.value } })
    this.markeer(veld, !!antwoord)
    if (!antwoord) return
    const data = await antwoord.json()
    const naam = this.element.querySelector("[data-template-naam]")
    if (naam && data.naam) naam.textContent = data.naam
  }

  bewerkTitel() {
    const blok = this.titelblokTarget
    blok.querySelector("[data-schema-titel]").hidden = true
    const invoer = blok.querySelector("[data-schema-titel-invoer]")
    invoer.hidden = false
    invoer.focus()
    invoer.select()
  }

  async bewaarTitel(event) {
    const invoer = event.target
    const antwoord = await this.stuur(this.titelblokTarget.dataset.url, { velden: { naam: invoer.value } })
    this.markeer(invoer, !!antwoord)
    if (!antwoord) return
    const data = await antwoord.json()
    this.titelblokTarget.querySelector("[data-schema-titel]").textContent = data.naam
  }

  sluitTitel(event) {
    if (event.type === "keydown") {
      event.preventDefault()
      event.target.blur()
      return
    }
    const blok = this.titelblokTarget
    event.target.hidden = true
    blok.querySelector("[data-schema-titel]").hidden = false
  }

  // ---- opslaan ------------------------------------------------------------

  async opslaan() {
    const actief = document.activeElement
    if (actief && this.element.contains(actief) && typeof actief.blur === "function") actief.blur()
    await new Promise((klaar) => setTimeout(klaar, 0))
    this.toonStatus(this.tekstenValue.wachten)
    await this.wachtrij
    if (this.fouten > 0) {
      this.toonStatus(this.tekstenValue.onbewaard)
      this.melding(this.tekstenValue.onbewaard)
      return
    }
    if (this.modusValue === "template") {
      this.afronden(false)
      return
    }
    const antwoord = await this.stuur(this.opslaanUrlValue, { methode: "POST" })
    if (!antwoord) return
    const data = await antwoord.json()
    this.melding(data.melding)
    this.toonStatus(this.tekstenValue.schema_opgeslagen)
  }

  async afronden(toch) {
    const antwoord = await this.stuur(this.opslaanUrlValue, { methode: "POST", velden: toch ? { toch: "1" } : {} })
    if (!antwoord) return
    const data = await antwoord.json()
    if (data.goed) {
      this.melding(data.melding)
      Turbo.visit(data.url)
      return
    }
    const dialoog = this.controleTarget
    const lijst = dialoog.querySelector("[data-lijst]")
    lijst.innerHTML = ""
    data.problemen.forEach((probleem) => {
      const item = document.createElement("li")
      item.textContent = probleem
      lijst.appendChild(item)
    })
    const keuze = await this.kies(dialoog)
    if (keuze === "toch") this.afronden(true)
  }

  voorVertrek(event) {
    if (this.bezig > 0 || this.fouten > 0) {
      event.preventDefault()
      event.returnValue = ""
    }
  }

  // ---- dialogen -----------------------------------------------------------

  vraag(titel, tekst) {
    const dialoog = this.bevestigTarget
    dialoog.querySelector("[data-titel]").textContent = titel
    dialoog.querySelector("[data-tekst]").textContent = tekst
    return this.kies(dialoog).then((keuze) => keuze === "ja")
  }

  kies(dialoog, tekst = null) {
    if (tekst !== null) {
      const plek = dialoog.querySelector("[data-tekst]")
      if (plek) plek.textContent = tekst
    }
    return new Promise((klaar) => {
      const sluiten = () => {
        dialoog.removeEventListener("close", sluiten)
        klaar(dialoog.returnValue)
      }
      dialoog.returnValue = ""
      dialoog.addEventListener("close", sluiten)
      if (typeof dialoog.showModal === "function") dialoog.showModal()
      else klaar(window.confirm(dialoog.textContent.trim()) ? "ja" : "")
    })
  }
}
